import React, { useState } from 'react';
import { navigate, RouteComponentProps, useLocation } from '@reach/router';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FormTextField from 'components/formComponents/FormTextField';
import { PostSetPassword } from './setPasswordApi';
import { SetPasswordPost } from './models';
import {
  AppPath,
  PASSWORD_MATCH_POLICY_ERROR_MESSAGE,
  PASSWORD_MIN_LENGTH,
  PASSWORD_REGEX,
} from 'appConstants';
import { Spinner } from 'components/spinner/Spinner';
import ConfirmationBox from 'components/confirmationBox/ConfirmationBox';
import PasswordStrengthMeter from 'components/passwordStrengthMeter/PasswordStrengthMeter';

const SUCCESS_MESSAGE = {
  title: 'Congratulations',
  message: 'Your password has been set.',
  buttonText: 'Sign in',
};

const SetPasswordForm: React.FC<RouteComponentProps> = () => {
  const [submitError, setSubmitError] = useState('');
  const [formIsPosted, setFormIsPosted] = useState(false);
  const location = useLocation();
  const passwordChangeRequired = !!(location.state as any)
    ?.passwordChangeRequired;

  const initialValues: SetPasswordPost = {
    passwordResetToken: '',
    newPassword: '',
    confirmNewPassword: '',
  };

  const handleSubmitForm = async (values: SetPasswordPost) => {
    setSubmitError('');

    const getURLToken = new URLSearchParams(window.location.search).get(
      'token'
    );
    const tokenVal = getURLToken === null ? '' : getURLToken;

    values.passwordResetToken = encodeURIComponent(tokenVal);

    PostSetPassword(values).then((response) => {
      if (!response.errors) {
        setFormIsPosted(true);
        setSubmitError('');
      } else {
        setFormIsPosted(false);
        setSubmitError('Set password failed, please try again');
      }
    });
  };

  const validationSchema = Yup.object({
    newPassword: Yup.string()
      .required('You must provide a password')
      .min(PASSWORD_MIN_LENGTH, PASSWORD_MATCH_POLICY_ERROR_MESSAGE)
      .matches(PASSWORD_REGEX, PASSWORD_MATCH_POLICY_ERROR_MESSAGE),
    confirmNewPassword: Yup.string()
      .required('You must confirm your new password')
      .oneOf([Yup.ref('newPassword'), null], 'Passwords must match'),
  });

  return (
    <div className="Form">
      {!formIsPosted ? (
        <Formik
          initialValues={initialValues}
          onSubmit={handleSubmitForm}
          validationSchema={validationSchema}
        >
          {({ isSubmitting, values }) => (
            <Form>
              <h1>Set Password</h1>
              <p className="SubTitle">
                {passwordChangeRequired
                  ? 'Please set a new password before you sign in.'
                  : 'Enter your password below'}
              </p>
              <div className="UserCredentialsSection">
                <FormTextField
                  field={'newPassword'}
                  label={'Password'}
                  type={'password'}
                  required={true}
                />
                <PasswordStrengthMeter password={values.newPassword} />
                <FormTextField
                  field={'confirmNewPassword'}
                  label={'Confirm Password'}
                  type={'password'}
                  required={true}
                />
              </div>
              {submitError && <div className="GeneralError">{submitError}</div>}
              <button
                className="Btn BtnOrange"
                type="submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? <Spinner color={'#ffffff'} /> : 'Set Password'}
              </button>
            </Form>
          )}
        </Formik>
      ) : (
        <div className="Success">
          <div className="ConfirmationBoxContainer">
            <ConfirmationBox
              {...SUCCESS_MESSAGE}
              buttonOnClick={() => navigate(AppPath.SIGN_IN)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SetPasswordForm;
